import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { pmSavedViewsService, type PmSavedView, type PmSavedViewScope } from '../../../services/projectManagement/savedViewsService';
import { pmSavedViewPreferencesService, type PmSavedViewPreferences } from '../../../services/projectManagement/savedViewPreferencesService';

interface PmSavedViewsControlProps {
  organizationId: string;
  scope: PmSavedViewScope;
  getCurrentPayload: () => Record<string, any>;
  applyPayload: (payload: Record<string, any>) => void;
}

const PmSavedViewsControl: React.FC<PmSavedViewsControlProps> = ({
  organizationId,
  scope,
  getCurrentPayload,
  applyPayload,
}) => {
  const [loading, setLoading] = useState(false);
  const [views, setViews] = useState<PmSavedView[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [prefs, setPrefs] = useState<PmSavedViewPreferences | null>(null);
  const didAutoApplyDefault = useRef(false);

  useEffect(() => {
    didAutoApplyDefault.current = false;
    setSelectedId('');
    setPrefs(null);
  }, [organizationId, scope]);

  const selectedView = useMemo(
    () => views.find(v => v.id === selectedId) || null,
    [views, selectedId]
  );

  const pinnedIndex = useMemo(() => {
    const m = new Map<string, number>();
    (prefs?.pins || []).forEach((p, idx) => {
      m.set(p.view_id, Number.isFinite(p.position) ? p.position : idx);
    });
    return m;
  }, [prefs]);

  const orderedViews = useMemo(() => {
    const list = [...views];
    list.sort((a, b) => {
      const ap = pinnedIndex.has(a.id);
      const bp = pinnedIndex.has(b.id);
      if (ap !== bp) return ap ? -1 : 1;

      if (ap && bp) {
        return (pinnedIndex.get(a.id) || 0) - (pinnedIndex.get(b.id) || 0);
      }

      return String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
    });
    return list;
  }, [views, pinnedIndex]);

  const fetchViews = useCallback(async () => {
    setLoading(true);
    try {
      const [viewsRes, prefsRes] = await Promise.all([
        pmSavedViewsService.list({ organization_id: organizationId, scope }),
        pmSavedViewPreferencesService.get({ organization_id: organizationId, scope }),
      ]);

      if (!viewsRes.error) {
        setViews(viewsRes.data || []);
      } else {
        setViews([]);
      }

      if (!prefsRes.error) {
        setPrefs(prefsRes.data || { default_view_id: null, pins: [] });
      } else {
        setPrefs({ default_view_id: null, pins: [] });
      }
    } catch {
      setViews([]);
      setPrefs({ default_view_id: null, pins: [] });
    } finally {
      setLoading(false);
    }
  }, [organizationId, scope]);

  useEffect(() => {
    void fetchViews();
  }, [fetchViews]);

  useEffect(() => {
    if (didAutoApplyDefault.current) return;
    const defaultId = prefs?.default_view_id || '';
    if (!defaultId) return;
    if (selectedId) return;
    const v = views.find(x => x.id === defaultId);
    if (!v) return;

    didAutoApplyDefault.current = true;
    setSelectedId(defaultId);
    applyPayload(v.payload || {});
  }, [prefs, views, selectedId, applyPayload]);

  const handleSelect = async (id: string) => {
    setSelectedId(id);
    const v = views.find(x => x.id === id);
    if (!v) return;
    applyPayload(v.payload || {});
  };

  const isPinned = !!(selectedView && pinnedIndex.has(selectedView.id));
  const isDefault = !!(selectedView && prefs?.default_view_id === selectedView.id);

  const handleSave = async () => {
    const name = window.prompt('Name this view');
    if (!name) return;
    const share = window.confirm('Share this view with your organization?');

    const payload = getCurrentPayload();

    const { error } = await pmSavedViewsService.upsert({
      organization_id: organizationId,
      scope,
      name,
      visibility: share ? 'org' : 'personal',
      payload,
    });

    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('View saved');
    await fetchViews();
  };

  const handleDelete = async () => {
    if (!selectedView) return;
    if (!window.confirm(`Delete view "${selectedView.name}"?`)) return;

    const { error } = await pmSavedViewsService.delete(selectedView.id, organizationId);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('View deleted');
    setSelectedId('');
    await fetchViews();
  };

  const handleTogglePin = async () => {
    if (!selectedView) return;

    if (isPinned) {
      const { error } = await pmSavedViewPreferencesService.unpin({
        organization_id: organizationId,
        scope,
        view_id: selectedView.id,
      });
      if (error) {
        toast.error(String(error));
        return;
      }
      toast.success('Unpinned');
      await fetchViews();
      return;
    }

    const nextPosition = (prefs?.pins || []).length;
    const { error } = await pmSavedViewPreferencesService.pin({
      organization_id: organizationId,
      scope,
      view_id: selectedView.id,
      position: nextPosition,
    });

    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Pinned');
    await fetchViews();
  };

  const handleToggleDefault = async () => {
    const next = isDefault ? null : (selectedView ? selectedView.id : null);
    const { error } = await pmSavedViewPreferencesService.setDefault({
      organization_id: organizationId,
      scope,
      view_id: next,
    });

    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success(isDefault ? 'Default cleared' : 'Default set');
    await fetchViews();
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={selectedId}
        onChange={(e) => void handleSelect(e.target.value)}
        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        disabled={loading}
      >
        <option value="">Views</option>
        {orderedViews.map(v => (
          <option key={v.id} value={v.id}>
            {(prefs?.default_view_id === v.id ? '✅ ' : '')}{(pinnedIndex.has(v.id) ? '⭐ ' : '')}{v.visibility === 'org' ? '🏢 ' : '👤 '}{v.name}
          </option>
        ))}
      </select>
      <button
        onClick={() => void handleTogglePin()}
        className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
        disabled={!selectedView}
      >
        {isPinned ? 'Unpin' : 'Pin'}
      </button>
      <button
        onClick={() => void handleToggleDefault()}
        className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
        disabled={!selectedView}
      >
        {isDefault ? 'Clear Default' : 'Set Default'}
      </button>
      <button
        onClick={() => void handleSave()}
        className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
      >
        Save
      </button>
      <button
        onClick={() => void handleDelete()}
        className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
        disabled={!selectedView}
      >
        Delete
      </button>
    </div>
  );
};

export default PmSavedViewsControl;
